/**
 * The dashboard: the opposite of Chase (Ed, 2026-11-03). Chase says who is late;
 * this says how many shifts in a period have a completed record, out of how many.
 *
 * Opens on the person's own practice. That is only a starting filter: office
 * staff can see every practice, and access is unchanged by it.
 */
import { getFunctions, httpsCallableFromURL, FunctionsError } from "firebase/functions";
import { app, appEnv } from "../firebase";
// Same population as omon-api's require_chase_operator: admin and planner.
import { type AuthClaims, canSeeConnect } from "../admin/claims";
import {
  ALL,
  type ClientRow,
  type CompletionResponse,
  type PracticeRow,
  isLive,
  pct,
  pctLabel,
  startingPractice,
  tally,
} from "./model";

const URL = "https://omon-api-myhmkw6jkq-nw.a.run.app/v1/chase_completion";
const PRACTICE_KEY = "dashboard.practice";
const PERIODS = [7, 14, 30];

const callable = httpsCallableFromURL<{ days: number }, CompletionResponse>(
  getFunctions(app, "europe-west2"),
  URL,
);

let days = 7;
let chosen: string | null = null;
let data: CompletionResponse | null = null;

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string,
  );
}

function container(): HTMLElement | null {
  return document.getElementById("dashboardView");
}

function message(title: string, body = ""): void {
  const el = container();
  if (el) el.innerHTML = `<div class="empty-state"><h3>${esc(title)}</h3>${body ? `<p>${esc(body)}</p>` : ""}</div>`;
}

function savedPractice(): string | null {
  try {
    return localStorage.getItem(PRACTICE_KEY);
  } catch {
    return null;
  }
}

function savePractice(p: string): void {
  try {
    localStorage.setItem(PRACTICE_KEY, p);
  } catch {
    /* a remembered filter is only a convenience */
  }
}

/** Panel ENTRY point (navigation.ts). */
export async function enterDashboard(claims: AuthClaims | null): Promise<void> {
  // ☠️ This is the page everyone lands on, and a refused call logs a console
  // error. Non-production planners reach omon-api at its production URL with a
  // token it refuses, so calling from there would fail every planner journey.
  if (appEnv !== "prod") {
    message("Shift records", "These numbers are only available in production.");
    return;
  }
  if (!canSeeConnect(claims)) {
    message("Welcome");
    return;
  }
  message("Loading shift records…");
  await load();
}

async function load(): Promise<void> {
  try {
    data = (await callable({ days })).data;
    if (chosen === null && data.available) {
      chosen = startingPractice(savedPractice(), data.defaultPractice, data.practices ?? []);
    }
    paint();
  } catch (e) {
    message("Couldn't load shift records", e instanceof FunctionsError ? e.message : String(e));
  }
}

function bar(n: number, of: number): string {
  return `<span class="adopt-bar"><span style="width:${pct(n, of)}%"></span></span>`;
}

function liveCells(counts: PracticeRow["counts"]): string {
  const t = tally(counts);
  return `<td>${bar(t.completed + t.byForm, t.shifts)}${t.completed + t.byForm} of ${t.shifts}
      <span class="chase-soft">${pctLabel(t.completed + t.byForm, t.shifts)}</span></td>
    <td>${t.onTime}</td><td>${t.late}</td><td>${t.byForm}</td>
    <td>${t.stillOwed}${t.overdue ? ` <span class="chase-soft">(${t.overdue} overdue)</span>` : ""}</td>
    <td>${t.noPlan}</td>`;
}

function preLiveCells(counts: PracticeRow["counts"]): string {
  const t = tally(counts);
  // No "No plan" figure here: plans aren't due until a practice goes live, so a
  // large number before then is expected and only reads as a problem.
  if (t.preLiveShifts === 0) {
    return `<td colspan="5" class="chase-soft">No shifts on a plan yet</td><td class="chase-soft">–</td>`;
  }
  return `<td colspan="5">${bar(t.doubleEntered, t.preLiveShifts)}${t.doubleEntered} of ${t.preLiveShifts}
      <span class="chase-soft">double entered, not yet required</span></td>
    <td class="chase-soft">–</td>`;
}

function practiceRow(p: PracticeRow): string {
  const label = isLive(p)
    ? `<span class="adopt-stage in-use">Live</span>`
    : `<span class="adopt-stage no-shifts">Not live</span>`;
  return `<tr><td class="chase-client">${esc(p.name)} ${label}</td>${isLive(p) ? liveCells(p.counts) : preLiveCells(p.counts)}</tr>`;
}

function clientRow(c: ClientRow, live: boolean): string {
  return `<tr><td class="chase-client">${esc(c.name ?? "Unnamed client")}</td>${live ? liveCells(c.counts) : preLiveCells(c.counts)}</tr>`;
}

function head(first: string): string {
  return `<thead><tr><th style="width:26%">${esc(first)}</th><th style="width:26%">Completed</th>
    <th>On time</th><th>Late</th><th>By MachForm</th><th>Still owed</th><th>No plan</th></tr></thead>`;
}

function paint(): void {
  const el = container();
  if (!el || !data) return;
  if (!data.available) {
    message("No shift record numbers yet", "They are written by the hourly chase job and appear after its next run.");
    return;
  }
  const all = data.practices ?? [];
  const current = all.find((p) => p.name === chosen);
  const shown = current ? [current] : all;
  const clients = current ? (data.clients ?? []).filter((c) => c.practice === current.name) : [];
  const made = data.generatedAt
    ? new Date(data.generatedAt).toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" })
    : "unknown";
  const options = [`<option value="${ALL}"${current ? "" : " selected"}>All practices</option>`,
    ...all.map((p) => `<option value="${esc(p.name)}"${p === current ? " selected" : ""}>${esc(p.name)}</option>`)].join("");
  const periods = PERIODS.map((d) => `<option value="${d}"${d === days ? " selected" : ""}>Last ${d} days</option>`).join("");

  el.innerHTML = `
    <div class="chase-head"><h2>Shift records</h2>
      <span class="chase-soft">updated ${esc(made)} · updates hourly</span></div>
    <div class="chase-filter">
      <label for="dashboardPractice">Practice</label><select id="dashboardPractice">${options}</select>
      <label for="dashboardDays">Period</label><select id="dashboardDays">${periods}</select>
    </div>
    <div class="chase-table-wrap"><table class="chase-table">${head("Practice")}
      <tbody>${shown.map(practiceRow).join("") || `<tr><td colspan="7" class="chase-soft">No shifts in this period.</td></tr>`}</tbody>
    </table></div>
    ${current && clients.length ? `<h3 style="margin:24px 0 8px">Clients</h3>
    <div class="chase-table-wrap"><table class="chase-table">${head("Client")}
      <tbody>${clients.map((c) => clientRow(c, isLive(current))).join("")}</tbody>
    </table></div>` : ""}
    <p class="chase-soft" style="margin-top:12px">A shift counts from the day its practice went live. Each carer's
      own record counts as one, so a shift with two carers counts twice. "No plan" is shown for live practices: those shifts couldn't take a record.</p>`;

  el.querySelector<HTMLSelectElement>("#dashboardPractice")?.addEventListener("change", (e) => {
    chosen = (e.target as HTMLSelectElement).value;
    savePractice(chosen);
    paint();
  });
  el.querySelector<HTMLSelectElement>("#dashboardDays")?.addEventListener("change", (e) => {
    days = Number((e.target as HTMLSelectElement).value);
    void load();
  });
}
